import { FormEvent, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  Button,
  Callout,
  Label,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  SaveIcon,
  TextInput,
  XIcon,
} from '../components/ui';
import { ApiError, api } from '../services/api';
import { t } from '../i18n';
import { AppHeader } from './AppHeader';
import { PlatformSidebar } from './PlatformSidebar';
import { usePageTitle } from './usePageTitle';

const SIDEBAR_COLLAPSED_KEY = 'balanceflow_platform_sidebar_collapsed';

function readCollapsedPreference(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function PlatformLayout() {
  const title = usePageTitle();
  const location = useLocation();
  const labels = t().platform;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    readCollapsedPreference,
  );
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen]);

  function toggleSidebarCollapsed() {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // ignore storage failures
      }
      return next;
    });
  }

  function openPasswordModal() {
    setPasswordOpen(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  }

  function closePasswordModal() {
    setPasswordOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  }

  async function handleChangePassword(event: FormEvent) {
    event.preventDefault();
    setPasswordError(null);
    if (newPassword.length < 8) {
      setPasswordError(labels.passwordMin);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(labels.passwordMismatch);
      return;
    }
    setPasswordSaving(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      closePasswordModal();
      setPasswordMessage(labels.passwordChanged);
    } catch (err) {
      setPasswordError(
        err instanceof ApiError ? err.message : t().common.error,
      );
    } finally {
      setPasswordSaving(false);
    }
  }

  return (
    <div className="flex h-screen overflow-hidden bg-surface text-ink">
      {mobileNavOpen ? (
        <button
          type="button"
          aria-label={t().nav.closeMenu}
          className="fixed inset-0 z-30 bg-slate-950/50 backdrop-blur-[1px] lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}

      <PlatformSidebar
        mobileOpen={mobileNavOpen}
        collapsed={sidebarCollapsed}
        onNavigate={() => setMobileNavOpen(false)}
        onToggleCollapse={toggleSidebarCollapsed}
        onChangePassword={openPasswordModal}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <AppHeader
          title={title}
          onOpenMenu={() => setMobileNavOpen(true)}
          onToggleSidebar={toggleSidebarCollapsed}
          sidebarCollapsed={sidebarCollapsed}
        />
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface p-3 sm:p-5 lg:p-6">
          {passwordMessage ? (
            <Callout variant="success" className="mb-4 shrink-0">
              {passwordMessage}
            </Callout>
          ) : null}
          <Outlet />
        </main>
      </div>

      <Modal isOpen={passwordOpen} onClose={closePasswordModal} size="sm">
        <form onSubmit={handleChangePassword}>
          <ModalHeader>
            <ModalTitle>{labels.changePasswordTitle}</ModalTitle>
          </ModalHeader>
          <ModalContent className="space-y-4">
            {passwordError ? (
              <Callout variant="danger">{passwordError}</Callout>
            ) : null}
            <div>
              <Label required>{labels.currentPassword}</Label>
              <TextInput
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div>
              <Label required>{labels.newPassword}</Label>
              <TextInput
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <div>
              <Label required>{labels.confirmNewPassword}</Label>
              <TextInput
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
          </ModalContent>
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closePasswordModal}
              disabled={passwordSaving}
            >
              <XIcon className="h-4 w-4" />
              {t().common.cancel}
            </Button>
            <Button type="submit" loading={passwordSaving}>
              <SaveIcon className="h-4 w-4" />
              {labels.savePassword}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
}
